import { Link } from 'react-router-dom';
import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';
import { brandInfo } from '@/mocks/brand';

const featuredArticles = articles.slice(0, 4);

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="高柳家——探索高柳家的故事与魅力 | 官方品牌网站"
        description="高柳家，承载着独特文化与深厚底蕴的品牌与家族符号。全面介绍高柳家的历史渊源、核心价值与产品/服务体验，感受高柳家所传递的品质与温度。"
        keywords="高柳家,高柳家历史,高柳家品牌,高柳家文化,高柳家特色,高柳家体验,高柳家官方介绍"
        canonicalUrl="https://takayanagi-family.com"
        ogTitle="高柳家——探索高柳家的故事与魅力"
        ogDescription="高柳家，承载着独特文化与深厚底蕴的品牌与家族符号。感受高柳家所传递的品质与温度。"
      />
      <SchemaOrg type="WebSite" data={{
        name: '高柳家',
        url: 'https://takayanagi-family.com',
        description: brandInfo.description,
        inLanguage: 'zh-Hans',
      }} />
      <SchemaOrg type="Organization" data={{
        name: '高柳家',
        url: 'https://takayanagi-family.com',
        description: brandInfo.description,
        foundingDate: '1750',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '京都市',
          addressRegion: '京都府',
          addressCountry: 'JP',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: brandInfo.contact.email,
          telephone: brandInfo.contact.phone,
        },
      }} />

      <main>
        {/* Hero */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Traditional%20Japanese%20wooden%20architecture%20with%20engawa%20veranda%20overlooking%20a%20serene%20zen%20garden%20with%20moss%20and%20raked%20gravel%2C%20soft%20morning%20sunlight%20filtering%20through%20cherry%20blossom%20trees%2C%20peaceful%20atmosphere%2C%20warm%20natural%20tones%2C%20elegant%20minimalist%20composition%2C%20artistic%20landscape%20photography&width=1800&height=900&seq=hero-takayanagi&orientation=landscape"
            alt="高柳家——传承九代的匠心之美"
            title="高柳家品牌官网——探索高柳家的故事与魅力"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4 w-full">
            <p className="text-sm md:text-base text-white/80 font-label tracking-widest">始于 1750 · 传承九代</p>
            <h1 className="mt-4 text-3xl md:text-5xl lg:text-6xl font-heading font-semibold text-white leading-tight">
              高柳家
            </h1>
            <p className="mt-4 md:mt-5 text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              承载着独特文化与深厚底蕴的品牌与家族符号<br className="hidden md:block" />
              以匠心与美学，诠释生活的温度与品质
            </p>
            <div className="mt-7 md:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/brand-story"
                className="whitespace-nowrap px-6 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-medium text-sm hover:bg-primary-600 transition-colors duration-200"
              >
                探索高柳家
              </Link>
              <Link
                to="/about"
                className="whitespace-nowrap px-6 py-2.5 rounded-md border border-white/40 text-white font-medium text-sm hover:bg-white/10 transition-colors duration-200"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Brand intro */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">About Takayanagi</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                跨越两个半世纪的匠心传承
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {brandInfo.description}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-10 md:mt-12">
              {brandInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 md:p-6 rounded-lg bg-background-100">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">{stat.value}</p>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Categories</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                探索高柳家的世界
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
                从品牌故事到匠心工艺，从生活美学到文化传承，每个维度都是高柳家不可或缺的篇章。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mt-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group block rounded-lg overflow-hidden bg-background-50 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-48 md:h-56 overflow-hidden">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      title={`高柳家${cat.name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 p-4 md:p-5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 flex items-center justify-center">
                          <i className={`${cat.icon} text-white text-base`}></i>
                        </div>
                        <h3 className="text-lg font-heading font-semibold text-white">{cat.name}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{cat.description}</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
                      浏览文章 <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </Link>
              ))}
              {/* Products category as a standalone card without image */}
              <Link
                to="/category/products"
                className="group block rounded-lg overflow-hidden border-2 border-dashed border-background-300 flex flex-col items-center justify-center p-8 md:p-10 min-h-[280px] transition-all duration-300 hover:border-primary-300 hover:bg-primary-50/30"
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-gift-line text-2xl"></i>
                </div>
                <h3 className="mt-4 text-lg font-heading font-semibold text-foreground-900">产品体验</h3>
                <p className="mt-2 text-sm text-foreground-500 text-center leading-relaxed max-w-xs">
                  体验高柳家精心打造的产品系列，每一件都承载着家族的品质承诺。
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
                  浏览文章 <i className="ri-arrow-right-line text-xs"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured articles */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Featured Articles</span>
                <h2 className="mt-2 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">精选文章</h2>
              </div>
              <Link
                to="/category/brand-story"
                className="whitespace-nowrap inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
              >
                查看全部 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-8">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group block rounded-lg overflow-hidden bg-background-100 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`高柳家——${article.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-2 py-0.5 rounded text-xs font-medium bg-background-50/90 text-foreground-700">
                      {categories.find((c) => c.slug === article.category)?.name}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Our Values</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                高柳家的核心价值
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-10">
              {brandInfo.values.map((v, i) => (
                <div key={i} className="p-5 md:p-6 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <span className="text-lg font-heading font-semibold">{i + 1}</span>
                  </div>
                  <h3 className="mt-4 text-base font-heading font-semibold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-50" id="faq">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">FAQ</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">常见问题</h2>
            </div>
            <div className="mt-10 space-y-3">
              {faqItems.map((faq, i) => (
                <div key={i} className="rounded-lg bg-background-100 overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <SchemaOrg type="FAQPage" data={{
              mainEntity: faqItems.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            }} />
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20interior%20of%20Japanese%20traditional%20house%20with%20wooden%20beams%20and%20soft%20lantern%20light%2C%20tea%20ceremony%20utensils%20on%20low%20table%2C%20inviting%20atmosphere%2C%20peaceful%20and%20elegant%20setting%2C%20golden%20hour%20light%20through%20paper%20screens&width=1800&height=600&seq=cta-home&orientation=landscape"
            alt="走进高柳家的世界"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white">走进高柳家的世界</h2>
            <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed">
              感受跨越两个半世纪的匠心之美，体验传统与现代交融的生活美学。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/about"
                className="whitespace-nowrap px-6 py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-medium text-sm hover:bg-primary-600 transition-colors duration-200"
              >
                了解高柳家
              </Link>
              <Link
                to="/category/craftsmanship"
                className="whitespace-nowrap px-6 py-2.5 rounded-md border border-white/50 text-white font-medium text-sm hover:bg-white/10 transition-colors duration-200"
              >
                探索匠心工艺
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}